import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { DspCreative } from "@front/m19-api-client";
import { DspStrategyEx } from "@front/m19-models/strategy/DspStrategyEx";
import { AccountSelectionService, DspCreativeService, DspStrategyService } from "@front/m19-services";
import {
  IButtonDirective,
  IIconComponent,
  ISelectMenuImports,
  MODAL_DATA,
  ModalRef,
  ModalService,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspCustomCreativesTableComponent } from "@m19-board/dsp/dsp-creatives/dsp-custom-creatives/dsp-custom-creatives-table/dsp-custom-creatives-table.component";
import {
  DspCustomCreativeCreationModalComponent,
  DspCustomCreativeCreationModalData,
} from "@m19-board/dsp/dsp-creatives/dsp-custom-creatives/dsp-custom-creative-creation/dsp-custom-creative-creation-modal.component";
import { filter, finalize, switchMap, take } from "rxjs";

@Component({
  selector: "app-dsp-add-custom-creative-modal",
  imports: [
    SpinnerComponent,
    TranslocoDirective,
    IButtonDirective,
    IIconComponent,
    DspCustomCreativesTableComponent,
    ISelectMenuImports,
  ],
  template: `
    <div class="flex h-full min-h-0 flex-col gap-4" *transloco="let t">
      @if (isLoading()) {
        <div class="flex flex-1 items-center justify-center">
          <ISpinner [display]="true" />
        </div>
      } @else {
        <div class="flex shrink-0 items-center justify-between gap-4">
          <div class="w-64 shrink-0">
            <ISelectMenu [(value)]="filterByRelatedToStrategyProducts" fitTriggerWidth>
              <ISelectTrigger class="flex w-full">
                <ISelectValue>
                  <div *iSelectValue="let value">
                    {{ strategyProductsLabel() }}
                  </div>
                </ISelectValue>
              </ISelectTrigger>
              <ISelectContent>
                <ISelectOption [value]="true">{{ strategyProductsLabel() }}</ISelectOption>
              </ISelectContent>
            </ISelectMenu>
          </div>
          <button iButton (click)="createCustomCreative()" [disabled]="isReadOnly()">
            <IIcon name="plus" />
            {{ t("dsp-creatives.new_creative") }}
          </button>
        </div>
        <div class="scrollbar min-h-0 flex-1 overflow-auto">
          <app-dsp-custom-creatives-table
            [dspCreatives]="filteredCreatives()"
            [(selectedCreatives)]="selectedCreatives"
          />
        </div>
      }
      <div class="flex shrink-0 justify-end border-t pt-4">
        <button
          iButton
          size="md"
          [loading]="isAttachingCreatives()"
          [disabled]="selectedCreatives().size === 0 || isAttachingCreatives()"
          (click)="addCreativeToStrategy()"
        >
          {{ t("dsp-creatives.add_creatives") }}
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAddCustomCreativeModalComponent {
  private readonly strategy = inject<DspStrategyEx>(MODAL_DATA);
  private readonly modalRef = inject(ModalRef);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dspCreativeService = inject(DspCreativeService);
  private readonly dspStrategyService = inject(DspStrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  private dspCreatives = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.dspCreativeService.getDspCreatives(am.dspAdvertiserId!, am.accountId, am.marketplace)),
    ),
  );

  filteredCreatives = computed(() => {
    return (this.dspCreatives() ?? []).filter((creative) => this.shouldDisplayCreative(creative));
  });

  protected readonly strategyProductsLabel = computed(() =>
    this.translocoService.translate("dsp-creatives.strategy_products_creatives"),
  );
  filterByRelatedToStrategyProducts = signal(true);

  isLoading = computed(() => !this.dspCreatives());
  isAttachingCreatives = signal(false);

  selectedCreatives = signal<Set<string>>(new Set<string>(), { equal: () => false });

  createCustomCreative() {
    if (this.isReadOnly()) {
      return;
    }
    this.modalService
      .openModal<DspCustomCreativeCreationModalData, boolean>(DspCustomCreativeCreationModalComponent, {
        size: "md",
        modalTitle: this.translocoService.translate("dsp-creatives.create_custom_creatives"),
        height: "fill",
        data: {
          isReadOnly: this.isReadOnly(),
          strategy: this.strategy,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((created) => created),
      )
      .subscribe(() => this.modalRef.close());
  }

  addCreativeToStrategy() {
    const selectedCreatives = Array.from(this.selectedCreatives().values()).map(
      (c) => (this.dspCreatives() ?? []).find((dc) => dc.adCreativeId === c)!,
    );

    this.isAttachingCreatives.set(true);
    this.dspStrategyService
      .attachCreativeToStrategy(this.strategy, selectedCreatives)
      .pipe(finalize(() => this.isAttachingCreatives.set(false)))
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("dsp-creatives.successfully_added_creatives_to_strategy"),
          );
          this.modalRef.close();
        },
        error: () => {
          this.isAttachingCreatives.set(false);
          this.toasterService.error(this.translocoService.translate("dsp-creatives.error_adding_creative_to_strategy"));
        },
      });
  }

  shouldDisplayCreative(creative: DspCreative): boolean {
    const creativeHasAlreadyBeenAdded = this.strategy.dspCustomCreatives?.some(
      (c) => c.adCreativeId === creative.adCreativeId,
    );
    const creativeIsRelatedToStrategyProduct = this.strategy.asins?.some((p) => p.asin === creative.asin);
    return !creativeHasAlreadyBeenAdded && creativeIsRelatedToStrategyProduct;
  }
}
